// src/components/molecules/SubmissionDetails/PurchaseSection.js
import React from "react";
import { Box, Button, Grid, Typography } from "@mui/material";
import ContributionAmountPicker from "./ContributionAmountPicker";

const centered = {
  display: "block",
  textAlign: "center",
  marginTop: "10px",
  fontSize: "0.9em",
  fontFamily: "basic-sans",
};

// The bar was duplicated in the goal-reached and purchasable branches; one
// component serves both, with `percentFunded` supplied by the caller.
const FundingProgress = ({ percentFunded, caption, theme }) => (
  <Box sx={{ width: "100%", marginTop: "10px" }}>
    <Box
      sx={{
        backgroundColor: theme.colors.palePurple,
        borderRadius: "50px",
        overflow: "hidden",
        position: "relative",
        height: "20px",
        width: "100%",
      }}
    >
      <Box
        sx={{
          width: `${percentFunded}%`,
          backgroundColor: theme.colors.green,
          height: "100%",
          transition: "width 0.3s ease-in-out",
        }}
      />
    </Box>
    <Typography variant="caption" sx={{ ...centered, marginTop: "5px" }}>
      {caption}
    </Typography>
  </Box>
);

const itemNoun = (contentType) => {
  if (contentType?.startsWith("video/")) return "videos";
  if (contentType?.startsWith("image/")) return "images";
  return "items";
};

const PurchaseSection = ({
  submissionDetails,
  isCrowdFunding,
  isSubmissionPending,
  isGoalReached,
  showPurchaseButton,
  amountUsd,
  handlingFee,
  maxAmount,
  onAmountChange,
  onPurchase,
  totalShares,
  remainingShares,
  pricePerShare,
  theme,
}) => {
  if (isSubmissionPending) {
    return (
      <Grid item>
        <Typography variant="caption" sx={centered}>
          Submission Pending
        </Typography>
      </Grid>
    );
  }

  if (isGoalReached) {
    return (
      <Grid item>
        <FundingProgress
          percentFunded={100}
          caption={`Goal Reached! The funding target of $${submissionDetails.fundingGoal} has been fully met.`}
          theme={theme}
        />
      </Grid>
    );
  }

  if (!showPurchaseButton) return <Grid item />;

  const format = (value) => new Intl.NumberFormat().format(value);
  const soldShares = totalShares - remainingShares;
  const percentFunded = totalShares > 0 ? (soldShares / totalShares) * 100 : 0;

  const caption = isCrowdFunding
    ? `$${format(pricePerShare * soldShares)} contributed so far, $${format(
        pricePerShare * remainingShares
      )} left to go!`
    : `${format(soldShares)} ${itemNoun(submissionDetails.contentType)} purchased so far, ${format(
        remainingShares
      )} still available!`;

  return (
    <Grid item>
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "stretch",
          gap: 1,
          width: "100%",
        }}
      >
        {isCrowdFunding && (
          <ContributionAmountPicker
            amountUsd={amountUsd}
            maxAmount={maxAmount}
            onAmountChange={onAmountChange}
            theme={theme}
          />
        )}

        <Button
          variant="contained"
          onClick={onPurchase}
          sx={{
            backgroundColor: theme.colors.green,
            color: "white",
            borderRadius: "5px",
            textTransform: "none",
            padding: "5px 20px",
            boxShadow: "none",
            "&:hover": {
              backgroundColor: theme.colors.blue,
              boxShadow: "none",
            },
          }}
        >
          {isCrowdFunding
            ? `Contribute: $${amountUsd.toFixed(2)} + $${handlingFee.toFixed(2)} Fee`
            : `Buy to Own: $${amountUsd.toFixed(2)} + $${handlingFee.toFixed(2)} Fee`}
        </Button>

        <FundingProgress percentFunded={percentFunded} caption={caption} theme={theme} />
      </Box>
    </Grid>
  );
};

export default PurchaseSection;
